import React, { useEffect } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Context Providers
import { AuthProvider, useAuth } from "./context/AuthContext";
import { CartProvider } from "./context/CartContext";
import { CurrencyProvider } from "./context/CurrencyContext";

// Components
import Header from "./components/Header";
import Footer from "./components/Footer";

// Pages
import Home from "./pages/Home";
import Products from "./pages/Products";
import ProductDetail from "./pages/ProductDetail";
import Login from "./pages/Login";
import Register from "./pages/Register";
import Cart from "./pages/Cart";
import Checkout from "./pages/Checkout";
import OrderHistory from "./pages/OrderHistory";
import AccountDetails from "./pages/AccountDetails";
import PayPalReturn from "./pages/PayPalReturn";
import MyPosReturn from "./pages/MyPosReturn";
import Contact from "./pages/Contact";

// Static Pages
import AboutUs from "./pages/static/AboutUs";
import PrivacyPolicy from "./pages/static/PrivacyPolicy";
import AfterSale from "./pages/static/AfterSale";

// Admin Pages
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProducts from "./pages/admin/Products";
import AdminOrders from "./pages/admin/Orders";
import AdminUsers from "./pages/admin/Users";
import UserManagement from "./pages/admin/UserManagement";
import AdminSettings from "./pages/admin/Settings";
import AdminEmails from "./pages/admin/Emails";
import AdminInbox from "./pages/admin/Inbox";
import ImageManager from "./pages/admin/ImageManager";

// Protected Route Component
import ProtectedRoute from "./components/ProtectedRoute";

function AppContent() {
  const { loading } = useAuth();

  useEffect(() => {
    const handlePointerDown = (event) => {
      if (!document.querySelector(".Toastify__toast")) {
        return;
      }

      const toastContainer = document.querySelector(".Toastify__toast-container");
      if (toastContainer?.contains(event.target)) {
        return;
      }

      toast.dismiss();
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="text-gold-500 text-xl mb-2">TabramEyewear</div>
          <div className="text-gray-400 text-sm">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <CurrencyProvider>
      <CartProvider>
        <div className="min-h-screen flex flex-col bg-black text-white">
          <Header />

        <main className="flex-grow">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/products" element={<Products />} />
            <Route path="/optical" element={<Products />} />
            <Route path="/sunglasses" element={<Products />} />
            <Route path="/accessories" element={<Products />} />
            <Route path="/childrens" element={<Products />} />
            <Route path="/products/:id" element={<ProductDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/contact" element={<Contact />} />

            {/* Static Pages */}
            <Route path="/about" element={<AboutUs />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/after-sale" element={<AfterSale />} />

            {/* Protected User Routes */}
            <Route
              path="/cart"
              element={
                <ProtectedRoute disallowAdmin>
                  <Cart />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout"
              element={
                <ProtectedRoute requireApproved disallowAdmin>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account"
              element={
                <ProtectedRoute disallowAdmin>
                  <AccountDetails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute requireApproved disallowAdmin>
                  <OrderHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/paypal/return"
              element={
                <ProtectedRoute requireApproved disallowAdmin>
                  <PayPalReturn />
                </ProtectedRoute>
              }
            />
            <Route
              path="/mypos/return"
              element={
                <ProtectedRoute requireApproved disallowAdmin>
                  <MyPosReturn />
                </ProtectedRoute>
              }
            />

            {/* Admin Routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/products"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminProducts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/orders"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminOrders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminUsers />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/users/:userId"
              element={
                <ProtectedRoute requireAdmin>
                  <UserManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/emails"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminEmails />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/inbox"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminInbox />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/settings"
              element={
                <ProtectedRoute requireAdmin>
                  <AdminSettings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/images"
              element={
                <ProtectedRoute requireAdmin>
                  <ImageManager />
                </ProtectedRoute>
              }
            />
          </Routes>
        </main>

          <Footer />

          {/* Toast Notifications */}
          <ToastContainer position="bottom-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable={false} pauseOnHover theme="dark" />
        </div>
      </CartProvider>
    </CurrencyProvider>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
